import React, { useState, useEffect, useRef } from 'react';
import { usersAPI, invitesAPI, seedAPI, brandingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Settings, Users, Shield, Database, Mail, Save, Loader2, UserPlus, Trash2, Send, Copy, Link, AlertTriangle, Clock, CheckCircle, Key, Activity, LogOut, Upload, Image as ImageIcon, X, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { SignatureBuilder, TestEmailButton } from '../components/SignatureBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SettingsPage = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'staff' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    tax_rate: '10',
    email_signature: '',
    default_payment_terms: '',
    default_payment_details: '',
  });
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [signatureBuilderOpen, setSignatureBuilderOpen] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchInvites();
    fetchBranding();
    if (user?.role === 'superuser') {
      fetchActivityLogs();
    }
  }, [user]);

  const fetchActivityLogs = async () => {
    try {
      const response = await usersAPI.getActivityLog({ limit: 50 });
      setActivityLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await brandingAPI.get();
      if (response.data) {
        setCompanySettings(prev => ({
          ...prev,
          company_name: response.data.company_name || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          address: response.data.address || '',
          email_signature: response.data.email_signature || '',
          default_payment_terms: response.data.default_payment_terms || '',
          default_payment_details: response.data.default_payment_details || '',
        }));
        if (response.data.logo_url) {
          setCurrentLogo(response.data.logo_url);
        }
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (PNG, JPEG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await brandingAPI.uploadLogo(formData);
      setCurrentLogo(response.data.logo_url);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      // Reset the input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http')) return logoPath;
    return `${API_URL}${logoPath}`;
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await invitesAPI.getAll();
      setInvites(response.data || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  const handleToggleUserActive = async (userId, currentStatus) => {
    try {
      await usersAPI.update(userId, { is_active: !currentStatus });
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await usersAPI.delete(userToDelete.id);
      toast.success(`User ${userToDelete.email} has been deleted`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteForm.email) {
      toast.error('Email is required');
      return;
    }

    setInviteLoading(true);
    try {
      const response = await invitesAPI.create(inviteForm);
      toast.success(`Invitation created for ${inviteForm.email}`);
      setShowInviteDialog(false);
      setInviteForm({ email: '', name: '', role: 'staff' });
      fetchInvites();
      
      // Copy invite link to clipboard
      const inviteLink = `${window.location.origin}/invite?token=${response.data.token}`;
      navigator.clipboard.writeText(inviteLink);
      toast.success('Invite link copied to clipboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSendInviteEmail = async (inviteId) => {
    try {
      await invitesAPI.sendEmail(inviteId);
      toast.success('Invitation email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleCopyInviteLink = (token) => {
    const inviteLink = `${window.location.origin}/invite?token=${token}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied to clipboard');
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await invitesAPI.delete(inviteId);
      toast.success('Invitation deleted');
      fetchInvites();
    } catch (error) {
      toast.error('Failed to delete invitation');
    }
  };

  const handleSaveSettings = async () => {
    setBrandingLoading(true);
    try {
      await brandingAPI.update({
        company_name: companySettings.company_name,
        email: companySettings.email,
        phone: companySettings.phone,
        address: companySettings.address,
        email_signature: companySettings.email_signature,
        default_payment_terms: companySettings.default_payment_terms,
        default_payment_details: companySettings.default_payment_details,
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await usersAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelAccount = async () => {
    try {
      await usersAPI.cancelAccount();
      toast.success('Account cancelled. You will be logged out.');
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel account');
    }
    setShowCancelDialog(false);
  };

  const handleSeedData = async () => {
    try {
      const response = await seedAPI.seedData();
      toast.success(`Sample data created: ${response.data.contractors_created} contractors, ${response.data.jobs_created} jobs`);
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const [seedingDemo, setSeedingDemo] = useState(false);
  const handleSeedDemoTenant = async (reset = false) => {
    if (reset && !window.confirm('Wipe the existing demo tenant and rebuild from scratch?')) return;
    setSeedingDemo(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/seed-demo-tenant${reset ? '?reset=true' : ''}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Seed failed');
      toast.success(`Demo tenant ready — ${data.counts.jobs} jobs, ${data.counts.quotes} quotes. Login: ${data.login.email} / ${data.login.password}`);
    } catch (error) {
      toast.error(`Demo seed failed: ${error.message}`);
    } finally {
      setSeedingDemo(false);
    }
  };

  const roleColors = {
    superuser: 'bg-red-500/20 text-red-400 border-red-500/30',
    admin: 'bg-primary/20 text-primary border-primary/30',
    staff: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    trade: 'bg-green-500/20 text-green-400 border-green-500/30',
    client: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage users, invitations, and company settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Settings */}
        <Card className="lg:col-span-2 bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Company Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Logo Upload Section */}
            <div className="space-y-3 pb-4 border-b border-border">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Company Logo
              </Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 border-2 border-dashed border-border rounded-sm flex items-center justify-center bg-muted/50 overflow-hidden">
                  {currentLogo ? (
                    <img 
                      src={getLogoUrl(currentLogo)} 
                      alt="Company Logo" 
                      className="w-full h-full object-contain"
                      data-testid="company-logo-preview"
                    />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    data-testid="logo-upload-input"
                  />
                  <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading}
                    data-testid="upload-logo-btn"
                  >
                    {logoUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPEG, GIF, or WebP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={companySettings.company_name}
                  onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                  placeholder="Your Company Pty Ltd"
                  className="bg-input border-border rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={companySettings.email}
                  onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                  placeholder="info@company.com"
                  className="bg-input border-border rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={companySettings.phone}
                  onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                  placeholder="1300 000 000"
                  className="bg-input border-border rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={companySettings.tax_rate}
                  onChange={(e) => setCompanySettings({ ...companySettings, tax_rate: e.target.value })}
                  className="bg-input border-border rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={companySettings.address}
                onChange={(e) => setCompanySettings({ ...companySettings, address: e.target.value })}
                placeholder="123 Main Street, City, State 1234"
                className="bg-input border-border rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Signature
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSignatureBuilderOpen(true)}
                    data-testid="open-signature-builder-btn"
                  >
                    <Wand2 className="h-4 w-4 mr-2" /> Build Signature
                  </Button>
                  <TestEmailButton
                    defaultEmail={user?.email || companySettings.email}
                    companyName={companySettings.company_name}
                  />
                </div>
              </div>
              <Textarea
                value={companySettings.email_signature}
                onChange={(e) => setCompanySettings({ ...companySettings, email_signature: e.target.value })}
                placeholder="Click 'Build Signature' to generate a professional HTML signature — or paste your own HTML / plain text here."
                className="bg-input border-border rounded-sm min-h-[160px] font-mono text-sm"
                rows={8}
                data-testid="email-signature-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Added to every outgoing email. <strong>Supports HTML</strong> — use the builder above for a guided setup, or paste your own HTML (e.g. from Gmail) for full custom branding.
              </p>
            </div>

            {/* Payment Terms Section */}
            <div className="pt-4 border-t border-border space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Default Quote Payment Settings</h3>
              <div className="space-y-2">
                <Label>Default Payment Terms</Label>
                <Textarea
                  value={companySettings.default_payment_terms}
                  onChange={(e) => setCompanySettings({ ...companySettings, default_payment_terms: e.target.value })}
                  placeholder="e.g., 50% deposit upon approval of quote and remaining to be paid on completion of project"
                  className="bg-input border-border rounded-sm min-h-[80px] text-sm"
                  rows={3}
                  data-testid="default-payment-terms"
                />
                <p className="text-xs text-muted-foreground">Default payment terms that appear on all quote PDFs.</p>
              </div>
              <div className="space-y-2">
                <Label>Bank Details</Label>
                <Textarea
                  value={companySettings.default_payment_details}
                  onChange={(e) => setCompanySettings({ ...companySettings, default_payment_details: e.target.value })}
                  placeholder="Account Name&#10;BSB: XXXXXX&#10;Account: XXXXXXXX"
                  className="bg-input border-border rounded-sm min-h-[100px] font-mono text-sm"
                  rows={4}
                  data-testid="default-bank-details"
                />
                <p className="text-xs text-muted-foreground">Bank details for payment that appear on all quote PDFs.</p>
              </div>
            </div>

            <Button onClick={handleSaveSettings} disabled={brandingLoading} className="uppercase font-bold tracking-wide rounded-sm">
              {brandingLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Settings</>}
            </Button>
          </CardContent>
        </Card>

        {/* Profile Card */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-lg font-bold">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <Badge className={`mt-1 ${roleColors[user?.role]} rounded-sm uppercase text-xs`}>
                  {user?.role}
                </Badge>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              {user?.company_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company</span>
                  <span>{user.company_name}</span>
                </div>
              )}
              {user?.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span>{user.phone}</span>
                </div>
              )}
            </div>
            <div className="mt-6 pt-4 border-t border-border space-y-2">
              <Button onClick={() => setShowPasswordDialog(true)} variant="outline" className="w-full rounded-sm" data-testid="change-password-btn">
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button onClick={() => setShowCancelDialog(true)} variant="outline" className="w-full rounded-sm text-destructive hover:text-destructive" data-testid="cancel-account-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Cancel Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demo Tenant Seeder - Superuser Only */}
      {user?.role === 'superuser' && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Demo Tenant
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Seeds an isolated <strong>ConstructOne Demo Builders</strong> tenant with 7 jobs, 8 contractors, quotes, POs and invoices so you can demo the app without touching real data. Login: <code className="text-foreground">demo@constructone.com.au</code> / <code className="text-foreground">Demo2026!</code>
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => handleSeedDemoTenant(false)}
                disabled={seedingDemo}
                data-testid="seed-demo-tenant-btn"
                className="rounded-sm"
              >
                {seedingDemo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                Seed Demo Tenant
              </Button>
              <Button
                onClick={() => handleSeedDemoTenant(true)}
                disabled={seedingDemo}
                data-testid="reset-demo-tenant-btn"
                variant="outline"
                className="rounded-sm"
              >
                {seedingDemo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                Wipe &amp; Rebuild Demo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Log - Superuser Only */}
      {user?.role === 'superuser' && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Activity Log (All Users)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activityLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <Activity className="h-12 w-12 mb-4 opacity-30" />
                <p>No activity logs yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityLogs.map((log) => (
                    <TableRow key={log.id} className="border-border">
                      <TableCell className="font-medium">{log.user_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-sm text-xs">
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{log.entity_type}</TableCell>
                      <TableCell className="max-w-xs truncate text-muted-foreground">
                        {log.details || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Users */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Invite Users
          </CardTitle>
          <Button onClick={() => setShowInviteDialog(true)} className="rounded-sm" data-testid="invite-user-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Send Invitation
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {invites.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <UserPlus className="h-12 w-12 mb-4 opacity-30" />
              <p>No pending invitations</p>
              <p className="text-sm">Click "Send Invitation" to invite new users</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => (
                  <TableRow key={invite.id} className="border-border">
                    <TableCell className="font-medium">{invite.email}</TableCell>
                    <TableCell>{invite.name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${roleColors[invite.role]} rounded-sm uppercase text-xs`}>
                        {invite.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {invite.used ? (
                        <Badge className="bg-green-500/20 text-green-400 rounded-sm">
                          <CheckCircle className="h-3 w-3 mr-1" /> Used
                        </Badge>
                      ) : isExpired(invite.expires_at) ? (
                        <Badge className="bg-red-500/20 text-red-400 rounded-sm">
                          <AlertTriangle className="h-3 w-3 mr-1" /> Expired
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400 rounded-sm">
                          <Clock className="h-3 w-3 mr-1" /> Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(invite.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {!invite.used && !isExpired(invite.expires_at) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyInviteLink(invite.token)}
                            className="text-muted-foreground hover:text-foreground"
                            title="Copy invite link"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendInviteEmail(invite.id)}
                            className="text-muted-foreground hover:text-foreground"
                            title="Send email"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteInvite(invite.id)}
                        className="text-destructive hover:text-destructive"
                        title="Delete invitation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* User Management */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              No users found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge className={`${roleColors[u.role]} rounded-sm uppercase text-xs`}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.company_name || '-'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={u.is_active}
                        onCheckedChange={() => handleToggleUserActive(u.id, u.is_active)}
                        disabled={u.id === user?.id}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {u.id !== user?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setUserToDelete(u); setShowDeleteDialog(true); }}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Seed Data */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            Sample Data
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-muted-foreground mb-4">
            Populate your system with sample contractors, jobs, and pricing items to get started quickly.
          </p>
          <Button onClick={handleSeedData} variant="outline" className="rounded-sm" data-testid="seed-data-btn">
            <Database className="h-4 w-4 mr-2" />
            Generate Sample Data
          </Button>
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Invite New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="user@example.com"
                className="bg-input border-border rounded-sm"
                data-testid="invite-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Name (Optional)</Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Smith"
                className="bg-input border-border rounded-sm"
                data-testid="invite-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                <SelectTrigger className="bg-input border-border rounded-sm" data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="trade">Trade/Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              An invitation link will be generated. The invite expires in 7 days.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInviteDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleCreateInvite} disabled={inviteLoading} className="rounded-sm" data-testid="confirm-invite">
              {inviteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link className="h-4 w-4 mr-2" />}
              Create Invite Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete <span className="font-bold text-foreground">{userToDelete?.email}</span>?
            </p>
            <p className="text-sm text-destructive mt-2">
              This action cannot be undone. The user will lose all access immediately.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} className="rounded-sm" data-testid="confirm-delete-user">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="bg-input border-border rounded-sm"
                data-testid="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                className="bg-input border-border rounded-sm"
                data-testid="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="bg-input border-border rounded-sm"
                data-testid="confirm-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPasswordDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={passwordLoading} className="rounded-sm" data-testid="submit-password">
              {passwordLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Key className="h-4 w-4 mr-2" />}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Account Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Cancel Account
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Are you sure you want to cancel your account?
            </p>
            <p className="text-sm text-destructive mt-2">
              Your account will be deactivated and you will be logged out immediately. Contact support if you wish to reactivate.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCancelDialog(false)} className="rounded-sm">
              Keep Account
            </Button>
            <Button variant="destructive" onClick={handleCancelAccount} className="rounded-sm" data-testid="confirm-cancel-account">
              <LogOut className="h-4 w-4 mr-2" />
              Yes, Cancel Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Builder Dialog */}
      <SignatureBuilder
        open={signatureBuilderOpen}
        onClose={() => setSignatureBuilderOpen(false)}
        branding={companySettings}
        currentLogo={currentLogo}
        onSave={(html) => setCompanySettings({ ...companySettings, email_signature: html })}
      />
    </div>
  );
};

export default SettingsPage;
